import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, MapPin, CalendarDays, Route as RouteIcon } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import JsonLd from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Reveal, Overline } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { RelatedGuides } from "@/components/RelatedGuides";
import { DESTINATIONS, PACKAGES } from "@/lib/data";
import { DESTINATION_DETAILS } from "@/lib/details";
import { useCms } from "@/context/ContentContext";
import { SITE, SITE_URL, waLink, imgSrcSet } from "@/lib/site";
import { dedupeBySlug } from "@/lib/cms";

export default function DestinationDetail() {
  const { slug } = useParams();
  const cms = useCms();
  const cmsDest = cms.destinations.find((d) => d.slug === slug);
  const dest = cmsDest || DESTINATIONS.find((d) => d.slug === slug);
  const detail = cmsDest || DESTINATION_DETAILS[slug];
  usePageMeta({
    title: dest ? `${dest.name} Tours & Travel Guide — Best Time, Highlights & Itineraries | TripTrix Voyages` : "Destination | TripTrix Voyages",
    description: dest ? `${dest.name} for the travel trade — highlights, best time to visit, sample itineraries & permits handled, with B2B net rates from TripTrix Voyages.` : "",
    image: dest ? dest.image : undefined,
    type: "article",
  });

  if (!cms.loaded && !(dest && detail)) {
    return <div className="min-h-screen grid place-items-center pt-32 text-[var(--tx-text-2)]">Loading…</div>;
  }
  if (!dest || !detail) return <Navigate to="/destinations" replace />;

  const allPackages = dedupeBySlug(PACKAGES, cms.packages);
  const allDestinations = dedupeBySlug(DESTINATIONS, cms.destinations);
  const related = allPackages.filter((p) => p.region.toLowerCase().includes(dest.name.toLowerCase()) || dest.name.toLowerCase().includes(p.region.toLowerCase().split(" ")[0])).slice(0, 3);
  const others = allDestinations.filter((d) => d.slug !== slug).slice(0, 4);

  const DMC_PATHS = {
    meghalaya: "/meghalaya-dmc",
    assam: "/assam-dmc",
    "arunachal-pradesh": "/arunachal-pradesh-dmc",
    sikkim: "/sikkim-dmc",
    nagaland: "/nagaland-dmc",
    bhutan: "/bhutan-dmc",
    darjeeling: "/darjeeling-dmc",
  };
  const dmcPath = DMC_PATHS[slug] || "/northeast-dmc";

  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristDestination",
        name: dest.name,
        description: dest.blurb,
        image: dest.image,
        touristType: "Leisure, Culture, Adventure",
        url: `${SITE_URL}/destinations/${slug}`,
        containedInPlace: { "@type": "Place", name: dest.region },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Destinations", item: `${SITE_URL}/destinations` },
          { "@type": "ListItem", position: 3, name: dest.name, item: `${SITE_URL}/destinations/${slug}` },
        ],
      },
      ...(detail.faqs && detail.faqs.length
        ? [{
            "@type": "FAQPage",
            mainEntity: detail.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }]
        : []),
    ],
  };

  return (
    <>
      <JsonLd data={jsonld} id="jsonld-destination" />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden" data-testid="destination-detail-hero">
        <img src={dest.image} srcSet={imgSrcSet(dest.image)} sizes="100vw" fetchPriority="high" alt={`${dest.name} — ${dest.kicker}`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--tx-bg)] via-[var(--tx-bg)]/30 to-[var(--tx-bg)]/50" />
        <div className="relative z-10 flex h-full items-end">
          <div className="mx-auto w-full max-w-[1400px] px-6 md:px-10 pb-14">
            <Breadcrumbs
              items={[
                { label: "Home", to: "/" },
                { label: "Destinations", to: "/destinations" },
                { label: dest.name },
              ]}
            />
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="text-xs uppercase tracking-[0.24em] text-[var(--tx-gold)]">{dest.region} · {dest.kicker}</span>
              <h1 className="mt-3 font-display font-light tracking-tight text-5xl md:text-7xl lg:text-8xl">{dest.name}</h1>
              <p className="mt-4 max-w-xl text-lg text-[var(--tx-text)]/80">{detail.lead || dest.blurb}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content flow + sticky trade enquiry rail (rail is desktop-only) */}
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:grid lg:grid-cols-12 lg:gap-14">
      <div className="lg:col-span-8 min-w-0">

      {/* Overview + second image */}
      <section className="py-20 md:py-28">
        <div className="grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <Reveal><Overline>Overview</Overline></Reveal>
            <div className="mt-6 space-y-5 text-lg text-[var(--tx-text-2)] leading-relaxed">
              {(detail.overview && detail.overview.length ? detail.overview : [dest.blurb]).map((p, i) => (<Reveal key={i} delay={i * 0.08}><p>{p}</p></Reveal>))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="relative h-[360px] md:h-[440px] overflow-hidden">
                <img src={detail.secondImage || dest.image} alt={`${dest.name} landscape`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Highlights */}
      {(detail.highlights || []).length > 0 && (
      <section className="py-20 bg-[var(--tx-bg-2)] border-y border-[var(--tx-border)]">
        <div>
          <Reveal><Overline>Highlights</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-display font-light tracking-tight text-4xl md:text-5xl">What makes {dest.name} unmissable</h2></Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {detail.highlights.map((h, i) => (
              <Reveal key={h.title} delay={(i % 2) * 0.08}>
                <div className="flex gap-5 border border-[var(--tx-border)] bg-[var(--tx-bg)] p-7 h-full">
                  <MapPin className="h-6 w-6 shrink-0 text-[var(--tx-gold)]" />
                  <div>
                    <h3 className="font-display text-2xl font-light">{h.title}</h3>
                    <p className="mt-2 text-[var(--tx-text-2)] leading-relaxed">{h.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Best time */}
      {(detail.bestTime || []).length > 0 && (
      <section className="py-20 md:py-28">
        <div>
          <Reveal><Overline>Best time to visit</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-display font-light tracking-tight text-4xl md:text-5xl">When to travel to {dest.name}</h2></Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {detail.bestTime.map((b, i) => (
              <Reveal key={b.season} delay={i * 0.08}>
                <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-7 h-full">
                  <div className="flex items-center gap-2 text-[var(--tx-gold)]"><CalendarDays className="h-4 w-4" /><span className="text-sm uppercase tracking-[0.18em]">{b.label}</span></div>
                  <div className="mt-3 font-display text-3xl font-light">{b.season}</div>
                  <p className="mt-3 text-[var(--tx-text-2)] leading-relaxed">{b.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Sample itineraries */}
      {(detail.itineraries || []).length > 0 && (
      <section className="py-20 bg-[var(--tx-bg-2)] border-y border-[var(--tx-border)]">
        <div>
          <Reveal><Overline>Sample itineraries</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-display font-light tracking-tight text-4xl md:text-5xl">Ways to travel {dest.name}</h2></Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {detail.itineraries.map((it, i) => (
              <Reveal key={it.name} delay={i * 0.08}>
                <div className="border border-[var(--tx-border)] bg-[var(--tx-bg)] p-8 h-full">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-2xl font-light">{it.name}</h3>
                    <span className="text-sm text-[var(--tx-gold)]">{it.days}</span>
                  </div>
                  <ol className="mt-6 space-y-3">
                    {(it.route || []).map((stop, k) => (
                      <li key={k} className="flex gap-3 text-[var(--tx-text-2)]">
                        <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-[var(--tx-border)] text-xs text-[var(--tx-gold)]">{k + 1}</span>
                        {stop}
                      </li>
                    ))}
                  </ol>
                  <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-[var(--tx-gold)] hover:gap-3 transition-all duration-300">Tailor this itinerary <ArrowUpRight className="h-4 w-4" /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Know before you go */}
      {(detail.knowBefore || []).length > 0 && (
      <section className="py-20 md:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><Overline>Know before you go</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="mt-5 font-display font-light tracking-tight text-4xl md:text-5xl">Practical essentials</h2></Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-4">
              {detail.knowBefore.map((k, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="flex gap-4 border-b border-[var(--tx-border)] pb-4 text-lg text-[var(--tx-text-2)]">
                    <Check className="h-5 w-5 shrink-0 text-[var(--tx-gold)]" /> {k}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>
      )}

      {/* FAQs (visible + FAQPage schema above) */}
      {(detail.faqs || []).length > 0 && (
      <section className="py-20 md:py-28 border-t border-[var(--tx-border)]" data-testid="dest-faqs">
        <div>
          <Reveal><Overline>FAQs</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="mt-5 font-display font-light tracking-tight text-4xl md:text-5xl">{dest.name} — questions travellers ask</h2></Reveal>
          <Reveal delay={0.15}>
            <Accordion type="single" collapsible className="mt-10">
              {detail.faqs.map((f, i) => (
                <AccordionItem key={i} value={`dest-faq-${i}`} className="border-[var(--tx-border)]" data-testid={`dest-faq-${i}`}>
                  <AccordionTrigger className="text-left text-lg font-light hover:text-[var(--tx-gold)]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[var(--tx-text-2)] leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
      )}

      </div>

      {/* Sticky trade enquiry rail — desktop only (mobile uses the bottom bar) */}
      <aside className="hidden lg:block lg:col-span-4">
        <div className="sticky top-28 border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-7" data-testid="dest-enquiry-rail">
          <Overline>Trade desk</Overline>
          <h3 className="mt-4 font-display text-3xl font-light leading-tight">Net rates for {dest.name}</h3>
          <p className="mt-3 text-sm text-[var(--tx-text-2)] leading-relaxed">
            Send your client's dates and group size — a workable {dest.name} itinerary and confidential net rates within 24 hours.
          </p>
          <ul className="mt-5 space-y-2.5 text-sm text-[var(--tx-text-2)]">
            {["Permits & border formalities handled", "White-labelled under your brand", "24/7 on-trip ground support"].map((b) => (
              <li key={b} className="flex gap-2.5"><Check className="h-4 w-4 shrink-0 mt-0.5 text-[var(--tx-gold)]" />{b}</li>
            ))}
          </ul>
          <a href="#enquire" data-testid="rail-get-rates" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--tx-gold)] px-6 py-3.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300">
            Get net rates <ArrowUpRight className="h-4 w-4" />
          </a>
          <a href={waLink(`Hi ${SITE.name}, I'd like net rates for ${dest.name}.`)} target="_blank" rel="noreferrer" data-testid="rail-whatsapp" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[var(--tx-border)] px-6 py-3 text-sm text-[var(--tx-text-2)] hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">
            WhatsApp the trade desk
          </a>
        </div>
      </aside>
      </div>

      {/* Related packages */}
      {related.length > 0 && (
        <section className="py-20 bg-[var(--tx-bg-2)] border-t border-[var(--tx-border)]">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10">
            <Reveal><Overline>Journeys featuring {dest.name}</Overline></Reveal>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {related.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08}>
                  <Link to={`/packages/${p.slug}`} className="group block border border-[var(--tx-border)] bg-[var(--tx-bg)] overflow-hidden hover:border-[var(--tx-gold)]/40 transition-colors duration-500">
                    <div className="relative h-48 overflow-hidden"><img src={p.image} alt={p.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105" /></div>
                    <div className="p-6">
                      <span className="text-xs uppercase tracking-[0.18em] text-[var(--tx-text-2)]">{p.days}</span>
                      <h3 className="mt-2 font-display text-xl font-light">{p.title}</h3>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-[var(--tx-gold)]">View itinerary <ArrowUpRight className="h-4 w-4" /></span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* For the travel trade — internal links */}
      <section className="border-t border-[var(--tx-border)] py-12" data-testid="dest-trade-links">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <p className="text-[var(--tx-text-2)] max-w-2xl">
            Selling {dest.name} to your clients? We handle it as a ground DMC — permits, transport, stays and guides at net rates.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to={dmcPath} className="inline-flex items-center gap-2 rounded-full border border-[var(--tx-border)] px-5 py-2.5 text-sm hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">
              {dest.name} DMC <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/tours" className="inline-flex items-center gap-2 rounded-full border border-[var(--tx-border)] px-5 py-2.5 text-sm hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">
              Tour packages
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-[var(--tx-gold)] px-5 py-2.5 text-sm font-medium text-[var(--tx-bg)] hover:bg-[var(--tx-text)] transition-colors duration-300">
              Get net rates
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="enquire" className="py-20 md:py-28 scroll-mt-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><Overline>Plan {dest.name}</Overline></Reveal>
            <Reveal delay={0.1}><h2 className="mt-6 font-display font-light tracking-tight text-4xl md:text-5xl leading-tight">Ready to explore {dest.name}?</h2></Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-lg text-[var(--tx-text-2)] max-w-md">Send your dates and client brief — our trade desk shares net rates and a workable {dest.name} itinerary within 24 hours.</p>
              <a href={waLink(`Hi ${SITE.name}, I'd like to plan a trip to ${dest.name}.`)} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 rounded-full border border-[var(--tx-border)] px-6 py-3 text-sm hover:border-[var(--tx-gold)] hover:text-[var(--tx-gold)] transition-colors duration-300">Or WhatsApp us</a>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              <div className="border border-[var(--tx-border)] bg-[var(--tx-bg-2)] p-7 md:p-9"><EnquiryForm /></div>
            </Reveal>
          </div>
        </div>
      </section>

      <RelatedGuides tokens={[dest.name, ...String(dest.region || "").split(" ")]} title={`${dest.name} guides & stories`} />

      {/* Explore more */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex items-center justify-between mb-8">
            <h3 className="font-display text-2xl font-light">Explore more destinations</h3>
            <Link to="/destinations" className="text-sm text-[var(--tx-gold)] hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {others.map((d) => (
              <Link key={d.slug} to={`/destinations/${d.slug}`} className="group relative h-40 overflow-hidden">
                <img src={d.image} alt={d.name} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-[var(--tx-bg)]/40 group-hover:bg-[var(--tx-bg)]/20 transition-colors duration-300" />
                <span className="absolute bottom-3 left-4 font-display text-xl font-light">{d.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile quick actions — desktop uses the sticky rail instead.
          Right padding keeps the floating chat/call buttons clear. */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden border-t border-[var(--tx-border)] bg-[var(--tx-bg)]/90 backdrop-blur-xl" data-testid="dest-mobile-cta">
        <div className="flex gap-3 p-3 pr-24">
          <a href="#enquire" data-testid="mobile-cta-rates" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--tx-gold)] px-5 py-3 text-sm font-medium text-[var(--tx-bg)]">
            Get net rates
          </a>
          <a href={waLink(`Hi ${SITE.name}, I'd like net rates for ${dest.name}.`)} target="_blank" rel="noreferrer" data-testid="mobile-cta-whatsapp" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[var(--tx-border)] px-5 py-3 text-sm text-[var(--tx-text-2)]">
            WhatsApp
          </a>
        </div>
      </div>
    </>
  );
}
